import { MessageOutlined } from "@ant-design/icons";
import { useCrestTheme } from "@crest/web-common/theme/crest-theme-preference.js";
import { App, Button, Tooltip } from "antd";
import {
	type ReactNode,
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
	type PlatformRuntimeContext,
	getApplicationAccessToken,
	loadPlatformRuntimeContext,
	subscribePlatformRuntimeContext,
} from "./app-runtime-context.js";
import {
	type FederatedLifecycleModule,
	type FederatedLifecycleProps,
	loadFederatedApp,
} from "./mf-runtime.js";

const AssistantContext = createContext<{
	available: boolean;
	open: () => void;
}>({
	available: false,
	open: () => undefined,
});

/** Portal 只装配入口和生命周期；聊天与工具执行由 Agent Remote 提供。 */
export function PortalAssistantProvider({
	children,
	enabled = __CREST_MF_ENABLED__,
}: { children: ReactNode; enabled?: boolean }) {
	const { message } = App.useApp();
	const { t } = useTranslation("portalNavigation");
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useCrestTheme();
	const [context, setContext] = useState<PlatformRuntimeContext>();
	const [activated, setActivated] = useState(false);
	const [open, setOpen] = useState(false);
	const [retryKey, setRetryKey] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const moduleRef = useRef<FederatedLifecycleModule>();
	const failedRef = useRef(false);
	const transitionRef = useRef<Promise<void>>(Promise.resolve());
	const latest = useRef({ location, navigate, open, message, t, theme });
	latest.current = { location, navigate, open, message, t, theme };
	const decision = context?.apps.agent;
	const release = enabled && decision?.allowed ? decision.release : undefined;
	const available = Boolean(release);

	useEffect(() => {
		if (!enabled) return;
		let disposed = false;
		const accept = (value: PlatformRuntimeContext) => {
			if (!disposed) setContext(value);
		};
		const unsubscribe = subscribePlatformRuntimeContext(accept);
		void loadPlatformRuntimeContext()
			.then(accept)
			.catch(() => undefined);
		return () => {
			disposed = true;
			unsubscribe();
		};
	}, [enabled]);

	const createProps = (
		container: HTMLElement,
		releaseId: string,
	): FederatedLifecycleProps => {
		const current = latest.current;
		return {
			container,
			releaseId,
			theme: current.theme,
			platform: {
				pathname: current.location.pathname,
				initialPathname: current.location.pathname,
				search: current.location.search,
				hash: current.location.hash,
				navigate: (path) => latest.current.navigate(path),
				getAccessToken: (options) =>
					getApplicationAccessToken("agent", options),
			},
			assistant: { open: current.open, onOpenChange: setOpen },
		};
	};

	useEffect(() => {
		const container = containerRef.current;
		let cancelled = false;
		let mounted: FederatedLifecycleModule | undefined;
		const load = async () => {
			if (cancelled || !container || !activated || !release) return;
			try {
				const loaded = await loadFederatedApp("agent", release);
				if (cancelled) return;
				if (!loaded.module.capabilities?.assistantWindow) {
					throw new Error(latest.current.t("assistantUpgradeRequired"));
				}
				mounted = loaded.module;
				await mounted.mount(createProps(container, loaded.remote.releaseId));
				if (cancelled) {
					await mounted.unmount({ container });
					mounted = undefined;
					return;
				}
				moduleRef.current = mounted;
				failedRef.current = false;
			} catch (reason) {
				await mounted?.unmount({ container });
				mounted = undefined;
				if (cancelled) return;
				failedRef.current = true;
				setOpen(false);
				latest.current.message.error(
					reason instanceof Error
						? reason.message
						: latest.current.t("assistantUnavailable"),
				);
			}
		};
		transitionRef.current = transitionRef.current
			.catch(() => undefined)
			.then(load);
		return () => {
			cancelled = true;
			moduleRef.current = undefined;
			transitionRef.current = transitionRef.current
				.catch(() => undefined)
				.then(async () => {
					if (mounted && container) await mounted.unmount({ container });
				});
		};
	}, [
		activated,
		release?.releaseId,
		release?.commitSha,
		release?.entry,
		retryKey,
	]);

	useEffect(() => {
		const container = containerRef.current;
		const module = moduleRef.current;
		if (!container || !module || !release) return;
		void Promise.resolve(
			module.update?.(createProps(container, release.releaseId)),
		).catch(() => {
			latest.current.message.error(latest.current.t("assistantUnavailable"));
		});
	}, [open, location.pathname, location.search, location.hash, theme]);

	return (
		<AssistantContext.Provider
			value={{
				available,
				open: () => {
					setActivated(true);
					setOpen(true);
					if (failedRef.current) setRetryKey((value) => value + 1);
				},
			}}
		>
			{children}
			<div ref={containerRef} data-portal-assistant="" />
		</AssistantContext.Provider>
	);
}

export function PortalAssistantButton() {
	const { available, open } = useContext(AssistantContext);
	const { t } = useTranslation("portalNavigation");
	if (!available) return null;
	return (
		<Tooltip title={t("openAssistant")}>
			<Button
				type="text"
				aria-label={t("openAssistant")}
				className="crest-header-action"
				onClick={open}
				icon={<MessageOutlined />}
			/>
		</Tooltip>
	);
}
